import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useRoute } from '../../router/router';
import { IconStar, IconRefresh, IconGear, IconTelegram } from '../../ui/icons';
import { bootOnce } from '../../bootstrap';
import { useI18n } from '../../bootstrap/i18n';
import type { ChatPlayerState, ExecuteResult, ModelMeta } from '../../chat/player/controller';
import { ChatPlayerController, parseModelMeta } from '../../chat/player/controller';
import { translateCommandText } from '../../chat/player/scenario';
import { requestAppBack } from '../../router/router';
import { trackPageView, trackChatStart, trackChatReset, trackPaywallView, trackPaywallCtaView, trackPaywallCtaClick, trackPaywallCtaDismiss, trackEvent } from '../../analytics';
import { ChatMessageList, ChatMessage } from './components/ChatMessageList';
import { ChatInput } from './components/ChatInput';
import { RelationshipBar } from './components/RelationshipBar';
import { CrowdfundPanel } from './CrowdfundPanel';
import { GoldenButton } from './GoldenButton';
import { SurpriseOfferOverlay, useSurpriseOffer } from './SurpriseOfferOverlay';
import { getClientSessionId } from '../../utils/clientSession';
import { reportHandledError } from '../../monitoring/uiErrorSink';
import { captureError } from '../../monitoring/errors';
import { apiFetch, apiJsonOrThrow, authedApiJsonOrThrow, authedFetchJson as authedFetchJsonUtil } from '../../utils/api';

import type { TopupSheetContext } from '../topup/TopupSheet';

type PremiumBenefitsView = {
  free_messages?: boolean;
  chat_messages_limit?: number;
  chat_messages_period?: 'day' | 'month';
  free_commands_count?: number;
  commands_limit?: number;
  commands_period?: 'day' | 'month';
  overage_mode?: 'use_credits' | 'block';
  hide_ad_models?: boolean;
  hide_surprise_offers?: boolean;
};

type PrivateShowState = {
  enabled: boolean;
  price_credits: number;
  message_price_credits?: number | null;
  command_price_credits?: number | null;
  entry_modal_enabled: boolean;
  active: boolean;
  grant_id?: string | null;
  source?: string | null;
  direct_telegram?: {
    enabled?: boolean;
    mode?: string;
    paid_media_capable?: boolean;
    price_stars_min?: number | null;
  } | null;
} | null;

export function ChatRoute(props: {
  onOpenTopup?: (ctx: TopupSheetContext) => void;
  isPremium?: boolean | null;
  isBillionaire?: boolean | null;
  subscriptionTier?: string | null;
  freeCreditsPurchaseEnabled?: boolean;
  messageCostCredits?: number;
  premiumBenefits?: PremiumBenefitsView | null;
  onOpenPremium?: (ctx: { context: any; buyCreditsCtx?: TopupSheetContext }) => void;
} = {}) {
  const route = useRoute();
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const commandsRef = useRef<HTMLDivElement>(null);
  const lastStateIdRef = useRef<string | null>(null);
  const controllerRef = useRef<ChatPlayerController | null>(null);
  const chatSessionIdRef = useRef<string | null>(null);
  const chatThreadIdRef = useRef<string | null>(null);
  const services = useMemo(() => bootOnce(), []);
  const { t, locale } = useI18n(services.i18n);
  const [playerState, setPlayerState] = useState<ChatPlayerState | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [playerReady, setPlayerReady] = useState(false);
  const welcomeTypeRef = useRef<'welcome' | 'welcome_back'>('welcome');
  const welcomeStartedRef = useRef<string | null>(null); // chatSessionId we already started welcome for
  
  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const pendingByRequestRef = useRef<Map<string, string>>(new Map()); // clientRequestId -> assistantMsgId
  const wsCursorRef = useRef<{ createdAt: string; id?: string } | null>(null); // server-side cursor (created_at,id)
  const chatWsRef = useRef<WebSocket | null>(null);
  const chatWsReconnectRef = useRef<any>(null);
  const chatWsLastAckAtRef = useRef<number>(0);
  const suppressStreamUpdateByRunRef = useRef<string>(''); // avoid spamming telemetry on stream->commit races
  // Mock relationship level (0-100)
  const [relationshipLevel, setRelationshipLevel] = useState(0);
  const relationshipMaxTrackedRef = useRef(false); // Track if we've logged relationship_max
  
  // Crowdfund state (for models without commands)
  const [crowdfundEnabled, setCrowdfundEnabled] = useState(false);
  const crowdfundCheckedRef = useRef<number | null>(null); // modelId we already checked
  
  // Counters for surprise offers
  const [commandsExecuted, setCommandsExecuted] = useState(0);
  const [messagesSent, setMessagesSent] = useState(0);
  const surpriseOffer = useSurpriseOffer(commandsExecuted, messagesSent);

  
  const isPremium = (props as any).isPremium as (boolean | null | undefined);
  const premiumBenefits = props.premiumBenefits || null;
  const messageCostCredits = Math.max(0, Math.trunc(Number(props.messageCostCredits ?? 1) || 0));
  const creditWord = messageCostCredits === 1 ? t('billing.credit', 'credit') : t('billing.credits', 'credits');
  const premiumChatIncluded = isPremium === true && premiumBenefits?.free_messages !== false;
  const premiumCommandsIncluded = isPremium === true && Number(premiumBenefits?.commands_limit ?? premiumBenefits?.free_commands_count ?? 0) > 0;
  const chatBillingLabel = premiumChatIncluded
    ? t('billing.included', 'Included')
    : `${messageCostCredits} ${creditWord}`;
  const chatBillingTone: 'free' | 'paid' | 'premium' = premiumChatIncluded ? 'premium' : (messageCostCredits > 0 ? 'paid' : 'free');
  const [privateShow, setPrivateShow] = useState<PrivateShowState>(null);
  const [privateShowPurchasing, setPrivateShowPurchasing] = useState(false);
  const privateShowIdempotencyRef = useRef<string>('');
  useEffect(() => {
    if (route.name !== 'chat') return;
    const modelId = Number(route.modelId || 0) || 0;
    if (!modelId) return;
    const timer = window.setTimeout(() => {
      const loc = services.i18n.getLocale();
      void services.i18n.loadLocale(loc, { force: true });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [route.name, route.name === 'chat' ? route.modelId : '', services]);
  const [privateBotOpening, setPrivateBotOpening] = useState(false);
  const [privateBotError, setPrivateBotError] = useState<string | null>(null);
  const [pendingInsufficient, setPendingInsufficient] = useState(false);
  const pendingInsufficientRef = useRef(false);
  const pendingMessagePaywallRef = useRef<null | { neededCredits: number; balance: number | null }>(null);
  const [actionCta, setActionCta] = useState<null | {
    kind: 'premium' | 'topup';
    track?: { reason: 'insufficient_credits' | 'premium_required'; neededCredits: number; balance: number | null; commandName: string };
    premiumCtx?: { context: any; buyCreditsCtx?: TopupSheetContext };
    topupCtx?: TopupSheetContext;
  }>(null);
  const actionCtaRef = useRef(actionCta);
  actionCtaRef.current = actionCta; // sync ref with state on every render
  const [pendingCommandPaywall, setPendingCommandPaywall] = useState<null | {
    reason: 'insufficient_credits' | 'premium_required';
    neededCredits: number;
    balance: number | null;
    commandName: string;
  }>(null);
  const pendingCommandPaywallRef = useRef<typeof pendingCommandPaywall>(null);
  const ctaDismissedRef = useRef(false);
  const commandClickLockRef = useRef(false);
  const activeStreamRef = useRef<null | {
    runId: string;
    assistantMsgId: string;
    abort: AbortController;
    startedAt: number;
    hasContent: boolean;
  }>(null);
  const lastCtaViewKeyRef = useRef<string>('');
  const enteredAtRef = useRef<number>(Date.now());
  useEffect(() => {
    if (route.name !== 'chat') return;
    enteredAtRef.current = Date.now();
    setPrivateBotError(null);
  }, [route.name, route.name === 'chat' ? route.modelId : '']);

  const privateBotUnlocked = relationshipLevel >= 100 && playerState?.hasPersonalTelegramBot === true;
  const privateShowIncluded = Boolean(privateShow?.enabled === true && privateShow?.active === true);
  const privateShowGateOpen = Boolean(privateShow?.enabled === true && privateShow?.active !== true);
  const privateShowMessageCostCredits = privateShowIncluded ? Math.max(0, Math.trunc(Number(privateShow?.message_price_credits || 0)) || 0) : 0;
  const privateShowCommandCostCredits = privateShowIncluded ? Math.max(0, Math.trunc(Number(privateShow?.command_price_credits || 0)) || 0) : 0;
  const privateShowMessageBillingLabel = privateShowIncluded
    ? (privateShowMessageCostCredits > 0 ? `${privateShowMessageCostCredits} ${privateShowMessageCostCredits === 1 ? t('billing.credit', 'credit') : t('billing.credits', 'credits')}` : t('private_show.included', 'Included'))
    : chatBillingLabel;

  const openPrivateBot = useCallback(async () => {
    if (!privateBotUnlocked || privateBotOpening) return;
    const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
    if (!Number.isFinite(modelIdNum) || modelIdNum <= 0) return;
    setPrivateBotOpening(true);
    setPrivateBotError(null);
    try {
      const chatThreadId = chatThreadIdRef.current;
      if (!chatThreadId) throw new Error('chat_thread_unavailable');
      const r = await authedFetchJsonUtil<any>(services, `/api/models/${encodeURIComponent(String(modelIdNum))}/private-show-bot-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'chat_model_badge',
          chatThreadId,
          client_click_id: globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        }),
      }, {
        ctx: { feature: 'chat', stage: 'private_bot_click', modelId: modelIdNum },
      });
      if (!r.ok || r.data?.tracking?.recorded !== true) {
        throw new Error(String(r.data?.code || r.data?.error || 'private_bot_tracking_failed'));
      }
      const openUrl = String((r as any)?.data?.open_url || '').trim();
      const directUrl = String((r as any)?.data?.direct_url || '').trim();
      const url = openUrl || directUrl;
      if (!url) throw new Error('private_bot_url_missing');
      const tg = services.telegram.tg;
      const isTelegramUrl = /^https?:\/\/(t\.me|telegram\.me)\//i.test(url);
      if (isTelegramUrl && tg?.openTelegramLink) tg.openTelegramLink(url);
      else if (tg?.openLink) tg.openLink(url);
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setPrivateBotError(t('chat.private_bot_failed', 'Could not open Telegram. Tap the avatar to try again.'));
      captureError({
        type: 'ui_error',
        message: 'chat.private_bot_click_failed',
        severity: 'warning',
        meta: {
          feature: 'chat',
          stage: 'private_bot_click',
          modelId: modelIdNum,
          chatSessionId: chatSessionIdRef.current || null,
          error: String((e as any)?.message || e || ''),
        },
      });
    } finally {
      setPrivateBotOpening(false);
    }
  }, [privateBotOpening, privateBotUnlocked, playerState?.modelId, route.name, route.name === 'chat' ? route.modelId : '', services, t]);

  useEffect(() => {
    if (route.name !== 'chat') return;
    const resume = (reason: string, opts?: { forceRecover?: boolean; verifyPlaybackMs?: number }) => {
      const delays = opts?.forceRecover ? [180, 700, 1600] : [80, 700];
      for (const delay of delays) {
        window.setTimeout(() => {
          try {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
          } catch {}
          try { updateChatVideoRenderHeight(); } catch {}
          void controllerRef.current?.handleLifecycleResume(reason, opts).catch(() => {});
        }, delay);
      }
    };

    const onVisibility = () => {
      try {
        if (document.visibilityState === 'visible') resume('visibilitychange', { forceRecover: true });
      } catch {}
    };
    const onPageShow = () => resume('pageshow', { forceRecover: true });
    const onFocus = () => resume('focus', { verifyPlaybackMs: 1200 });

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);

    const tg: any = services.telegram.tg;
    const onActivated = () => resume('telegram_activated', { forceRecover: true });
    try { tg?.onEvent?.('activated', onActivated); } catch {}

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      try { tg?.offEvent?.('activated', onActivated); } catch {}
    };
  }, [route.name, route.name === 'chat' ? route.modelId : '', services.telegram.tg]);

  // Track CTA overlay view once per appearance (funnel step before paywall_view)
  useEffect(() => {
    const cta = actionCta;
    if (!cta) return;
    const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
    const chatSessionId = chatSessionIdRef.current || null;
    const tr = cta.track || null;
    const key = [
      String(cta.kind),
      String(tr?.reason || ''),
      String(tr?.neededCredits ?? ''),
      String(tr?.balance ?? ''),
      String(tr?.commandName ?? ''),
      String(modelIdNum || ''),
      String(chatSessionId || ''),
    ].join('|');
    if (lastCtaViewKeyRef.current === key) return;
    lastCtaViewKeyRef.current = key;
    try {
      trackPaywallCtaView({
        origin: 'chat',
        surface: 'chat_actions_overlay',
        ctaKind: cta.kind,
        modelId: modelIdNum || undefined,
        chatSessionId,
        reason: tr?.reason,
        neededCredits: tr?.neededCredits ?? null,
        balance: tr?.balance ?? null,
        commandName: tr?.commandName ?? null,
      });
    } catch {}
  }, [
    actionCta?.kind,
    (actionCta as any)?.track?.reason,
    (actionCta as any)?.track?.neededCredits,
    (actionCta as any)?.track?.balance,
    (actionCta as any)?.track?.commandName,
    playerState?.modelId,
    route.name,
    route.name === 'chat' ? route.modelId : '',
  ]);

  // Persist relationship level to thread metadata (best-effort)
  useEffect(() => {
    if (route.name !== 'chat') return;
    const threadId = String(chatThreadIdRef.current || '').trim();
    if (!threadId) return;
    const tId = window.setTimeout(() => {
      try {
        authedFetchJsonUtil(services, `/api/chat/thread/${encodeURIComponent(threadId)}/metadata`, {
          method: 'POST',
          body: JSON.stringify({ patch: { relationship_level: relationshipLevel } }),
        }).catch(() => {});
      } catch {}
    }, 900);
    return () => window.clearTimeout(tId);
  }, [relationshipLevel, route.name]);

  // Track quest progress when relationship reaches 100%
  useEffect(() => {
    if (route.name !== 'chat') return;
    if (relationshipLevel >= 100 && !relationshipMaxTrackedRef.current) {
      relationshipMaxTrackedRef.current = true;
      // Fire-and-forget quest progress tracking
      authedFetchJsonUtil(services, '/api/quests/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_type: 'relationship_max', delta: 1 }),
      }).catch(() => {});
    }
  }, [relationshipLevel, route.name, services]);

  const debugEnabled = useMemo(() => {
    try {
      // Only on TEST. Never show on PROD.
      if (services.config.env !== 'test') return false;
      const q = new URLSearchParams(window.location.search);
      // Allow disabling explicitly (useful even on test).
      const flag = q.get('debugPlayer');
      if (flag === '0') return false;
      return true;
    } catch {
      return services.config.env === 'test';
    }
  }, [services.config.env]);

  function updateChatVideoRenderHeight() {
    const shell = shellRef.current;
    const media = mediaRef.current;
    const a = videoARef.current;
    const b = videoBRef.current;
    if (!shell || !media || !a || !b) return;
    const front = (a.style.zIndex === '2' ? a : (b.style.zIndex === '2' ? b : a));
    const vw = front.videoWidth || 0;
    const vh = front.videoHeight || 0;
    if (!vw || !vh) return;
    const rect = media.getBoundingClientRect();
    const cw = rect.width || 0;
    const ch = rect.height || 0;
    if (!cw || !ch) return;
    const videoAr = vw / vh;
    const containerAr = cw / ch;
    const renderH = containerAr > videoAr ? ch : cw / videoAr;
    shell.style.setProperty('--chat-video-render-h', `${Math.round(renderH)}px`);
  }

  async function authedFetchJson<T = any>(path: string, init?: RequestInit): Promise<
    | { ok: true; data: T }
    | { ok: false; status: number; data: any; err: any }
  > {
    try {
      const data = await authedApiJsonOrThrow<T>(services, path, init, { timeoutMs: 5000, ctx: { feature: 'chat', stage: 'authedFetchJson' } })
      return { ok: true, data: data as any }
    } catch (e: any) {
      const status = (e && typeof e === 'object' && 'status' in e) ? Number((e as any).status || 0) : 0
      return {
        ok: false,
        status: Number.isFinite(status) ? status : 0,
        data: { error: e?.code || 'request_failed', message: e?.message || String(e) },
        err: e,
      }
    }
  }

  useEffect(() => {
    if (route.name !== 'chat') return;
    let cancelled = false;

    (async () => {
      // Reset per-open state: don't show history / allow input until player is ready
      setPlayerReady(false);
      setMessages([]);
      setPrivateShow(null);
      privateShowIdempotencyRef.current = '';
      // IMPORTANT (MVP): do not load/render prior thread history on re-entry.
      // We enforce this via a server-side cursor based on chat session start timestamp.
      wsCursorRef.current = null;
      if (cancelled) return;
      const modelIdNum = Number(route.name === 'chat' ? route.modelId : 0);
      if (!Number.isFinite(modelIdNum) || modelIdNum <= 0) return;

      // Meta is a public (unauthed) endpoint — start it immediately, no JWT wait.
      // It runs in parallel with the authed bootstrap below.
      const metaP = apiJsonOrThrow(`/api/models/${encodeURIComponent(String(modelIdNum))}/meta`, { method: 'GET' }, {
        feature: 'chat_player',
        stage: 'load_model_meta',
        modelId: modelIdNum,
      }).then((out: any) => parseModelMeta(out?.data, modelIdNum))
        .catch(() => null as ModelMeta | null);

      // Bootstrap: combined endpoint, automatic fallback to legacy if unavailable.
      chatThreadIdRef.current = null;
      chatSessionIdRef.current = null;
      let bootPrivateShowState: any = null;
      try {
        let bootData: any = null;

        const rBoot = await authedFetchJson<any>('/api/chat/bootstrap', {
          method: 'POST',
          body: JSON.stringify({
            modelId: modelIdNum,
            clientSessionId: getClientSessionId(),
            locale: services.i18n.getLocale(),
          }),
        });

        if (rBoot.ok) {
          bootData = (rBoot.data as any) || {};
        } else {
          const rThread = await authedFetchJson<any>('/api/chat/thread/ensure', {
            method: 'POST',
            body: JSON.stringify({ modelId: modelIdNum }),
          });
          if (!rThread.ok) throw (rThread as any).err || new Error(String((rThread.data as any)?.error || 'thread_ensure_failed'));
          const td = (rThread.data as any) || {};

          const rSession = await authedFetchJson<any>('/api/chat/session/ensure', {
            method: 'POST',
            body: JSON.stringify({
              chatThreadId: td.chatThreadId,
              modelId: modelIdNum,
              clientSessionId: getClientSessionId(),
              locale: services.i18n.getLocale(),
            }),
          });
          if (!rSession.ok) throw (rSession as any).err || new Error(String((rSession.data as any)?.error || 'session_ensure_failed'));
          const sd = (rSession.data as any) || {};

          bootData = {
            chatThreadId: td.chatThreadId,
            chatSessionId: sd.chatSessionId,
            started_at: sd.started_at,
            metadata: td.metadata,
            has_dialog: td.has_dialog,
            private_show: sd.private_show || td.private_show || null,
          };
        }

        chatThreadIdRef.current = String(bootData.chatThreadId || '');
        chatSessionIdRef.current = String(bootData.chatSessionId || '');
        bootPrivateShowState = (bootData.private_show && typeof bootData.private_show === 'object') ? bootData.private_show : null;
        setPrivateShow(bootPrivateShowState);
        try {
          const startedAt = String(bootData.started_at || '').trim();
          if (startedAt) wsCursorRef.current = { createdAt: startedAt };
        } catch {}

        const hasDialog = bootData.has_dialog === true;
          welcomeTypeRef.current = hasDialog ? 'welcome_back' : 'welcome';
          welcomeStartedRef.current = null;

        const rel = Number((bootData.metadata?.relationship_level) ?? 10);
        setRelationshipLevel(Number.isFinite(rel) ? Math.max(0, Math.min(100, Math.round(rel))) : 10);
        relationshipMaxTrackedRef.current = rel >= 100;
      } catch (e) {
        setInitError(reportHandledError({
          err: e,
          kind: 'api',
          code: 'chat.bootstrap_failed',
          userMessage: String((e as any)?.message || e || 'Failed to start chat'),
          severity: 'error',
          context: { feature: 'chat', stage: 'bootstrap', modelId: modelIdNum },
        }));
        if (debugEnabled) setDebugOpen(true);
        console.error('chat bootstrap failed', e);
        return;
      }

      // Await pre-fetched meta (likely already resolved while bootstrap was in-flight).
      // If the parallel fetch failed, controller.init() will retry it internally.
      const prefetchedMeta = await metaP;

      const videoA = videoARef.current;
      const videoB = videoBRef.current;
      if (!videoA || !videoB) return;

      try {
        videoA.onloadedmetadata = () => updateChatVideoRenderHeight();
        videoB.onloadedmetadata = () => updateChatVideoRenderHeight();
      } catch {}

      const controller = new ChatPlayerController({
        services,
        modelId: modelIdNum,
        chatSessionId: String(chatSessionIdRef.current || ''),
        videoA,
        videoB,
        onState: setPlayerState,
        locale: () => services.i18n.getLocale(),
      });
      controllerRef.current = controller;
      try {
        setInitError(null);
        await controller.init({ prefetchedMeta });
        if (cancelled) return;
        setPlayerReady(true);

        void authedFetchJsonUtil(services, '/api/quests/progress', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action_type: 'visit_models', delta: 1, model_id: modelIdNum }),
        }).catch(() => {});

        // Welcome message: sequential after playerReady so WS is already connected
        // and there's no race between HTTP stream and WS snapshot.
        try {
          const chatThreadId = String(chatThreadIdRef.current || '').trim();
          const chatSessionId = String(chatSessionIdRef.current || '').trim();
          const wType = welcomeTypeRef.current;
          const bootPrivateShowGateOpen = Boolean(bootPrivateShowState?.enabled === true && bootPrivateShowState?.active !== true);
          if (chatThreadId && chatSessionId && !bootPrivateShowGateOpen && welcomeStartedRef.current !== chatSessionId) {
            welcomeStartedRef.current = chatSessionId;
            const welClientRequestId = `${wType}:${chatSessionId}`;
            const runMsgId = `welcome_${wType}_${chatSessionId}`;
            const rWel = await authedFetchJson<any>('/api/chat/welcome/start', {
              method: 'POST',
              body: JSON.stringify({
                chatThreadId,
                chatSessionId,
                type: wType,
                uiLocale: services.i18n.getLocale(),
              }),
            });
            if (!rWel.ok && String((rWel.data as any)?.error || '') === 'private_show_required') {
              const ps = (rWel.data as any)?.private_show;
              if (ps && typeof ps === 'object') setPrivateShow(ps);
            }
            const welRunId = rWel.ok ? String((rWel.data as any)?.runId || '').trim() : '';
            if (welRunId) {
              pendingByRequestRef.current.set(welClientRequestId, runMsgId);
              setMessages((prev) => {
                const ids = new Set(prev.map((m) => String(m.id)));
                if (ids.has(runMsgId)) return prev;
                return [...prev, { id: runMsgId, role: 'model', content: '', timestamp: Date.now() } as any];
              });
              await streamRunToMessage(welRunId, runMsgId);
              pendingByRequestRef.current.delete(welClientRequestId);
            }
          }
        } catch {}
      } catch (e) {
        const msg = String(e);
        // Extra one-shot telemetry: count true "MSE unavailable" devices.
        // We do NOT implement native fallback (per decision) — only measure prevalence.
        try {
          const s = String((e as any)?.message || '');
          if (s.includes('MSE/MMS not available')) {
            const csid = getClientSessionId();
            captureError({
              type: 'player.mse_unavailable',
              message: 'MSE/MMS not available',
              severity: 'warning',
              meta: {
                feature: 'chat',
                stage: 'player_init',
                modelId: modelIdNum,
                chatSessionId: chatSessionIdRef.current || null,
                incident_id: `mse_unavailable:${csid}`,
                ...(typeof (e as any)?.telemetry === 'object' ? (e as any).telemetry : {}),
              },
            });
          }
        } catch {}
        setInitError(reportHandledError({
          err: e,
          kind: 'player',
          code: 'chat.player_init_failed',
          userMessage: msg,
          severity: 'error',
          context: { feature: 'chat', stage: 'player_init', modelId: modelIdNum, chatSessionId: chatSessionIdRef.current || null },
        }));
        setPlayerReady(false);
        // On TEST keep debug modal open so we can copy logs quickly.
        if (debugEnabled) setDebugOpen(true);
        // eslint-disable-next-line no-console
        console.error('chat player init failed', e);
      }

      // Analytics: track chat start
      trackPageView('chat', { model_id: modelIdNum });
      trackChatStart(modelIdNum, undefined, chatSessionIdRef.current || undefined);

    })();

    const onResize = () => updateChatVideoRenderHeight();
    const onOrientationChange = () => {
      // Delayed recalculations for orientation change
      updateChatVideoRenderHeight();
      setTimeout(updateChatVideoRenderHeight, 100);
      setTimeout(updateChatVideoRenderHeight, 300);
      setTimeout(updateChatVideoRenderHeight, 500);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientationChange);
    window.addEventListener('aicams-layout-updated', onResize);

    return () => {
      cancelled = true;
      try { controllerRef.current?.destroy(); } catch {}
      controllerRef.current = null;
      chatSessionIdRef.current = null;
      chatThreadIdRef.current = null;
      setPlayerState(null);
      setInitError(null);
      try {
        if (chatWsReconnectRef.current) window.clearTimeout(chatWsReconnectRef.current);
        chatWsReconnectRef.current = null;
      } catch {}
      try { chatWsRef.current?.close(); } catch {}
      chatWsRef.current = null;
      pendingByRequestRef.current.clear();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrientationChange);
      window.removeEventListener('aicams-layout-updated', onResize);
    };
  }, [route.name, route.name === 'chat' ? route.modelId : '']);

  if (route.name !== 'chat') return null;

  const onReset = async () => {
    try {
      const modelId = playerState?.modelId;
      if (modelId) trackChatReset(modelId, playerState?.modelName, chatSessionIdRef.current || undefined);
    } catch {}

    // Reset affects only the player/video state; it must NOT reset chat history.
    void controllerRef.current?.reset().catch(() => {});

    // Record the reset request; the reply must not assume playback has completed.
    try {
      if (!playerReady) return;
      const chatThreadId = String(chatThreadIdRef.current || '').trim();
      const chatSessionId = String(chatSessionIdRef.current || '').trim();
      if (!chatThreadId || !chatSessionId) return;

      const clientRequestId = globalThis.crypto?.randomUUID
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const assistantMsgId = `reset_${clientRequestId}`;

      // Add only a model placeholder (no UI_TEXT/system bubble requested).
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => String(m.id)));
        const next = prev.slice();
        if (!ids.has(assistantMsgId)) next.push({ id: assistantMsgId, role: 'model', content: '', timestamp: Date.now() } as any);
        return next;
      });

      const r = await authedFetchJson<any>('/api/chat/player-reset/start', {
        method: 'POST',
        body: JSON.stringify({ chatThreadId, chatSessionId, clientRequestId, uiLocale: services.i18n.getLocale() }),
      });
      if (!r.ok) throw (r as any).err || new Error(String((r.data as any)?.error || 'reset_failed'));
      const runId = String((r.data as any)?.runId || '').trim();
      if (runId) {
        await streamRunToMessage(runId, assistantMsgId);
      } else {
        setMessages((prev) => prev.filter((m) => String(m.id) !== assistantMsgId));
      }
    } catch {
      // Best-effort: remove placeholder on failure
      try {
        setMessages((prev) => prev.filter((m) => !String(m.id).startsWith('reset_')));
      } catch {}
    }
  };
  const onBack = () => requestAppBack();
  const onToggleDebug = () => setDebugOpen((v) => !v);

  const commands = playerState?.commands || [];
  const purchased = new Set(playerState?.purchasedCommands || []);
  const isBusy = !!playerState?.isBusy;
  // Actions must be locked not only during player transitions (isBusy),
  // but also while we are waiting to show a paywall CTA (pending*) or while CTA overlay is visible.
  const actionsLocked = Boolean(isBusy || actionCta || pendingInsufficient || pendingCommandPaywall || privateShowGateOpen || privateShowPurchasing);
  // Immediate UI lock: prevents double-taps before state update disables buttons.
  useEffect(() => {
    if (!actionsLocked) commandClickLockRef.current = false;
  }, [actionsLocked]);
  const paywall = playerState?.paywall?.open ? playerState.paywall : null;
  // If player requests paywall (blocked command), delay showing paywall until after model reply finishes typing.
  useEffect(() => {
    if (!paywall) return;
    const needed = Number((paywall as any).needed || 0);
    const cmdName = String((paywall as any).commandName || '').trim();
    const reason = String((paywall as any).reason || 'insufficient_credits') as any;

    if (reason === 'private_show_required') {
      const ps = (paywall as any).private_show;
      if (ps && typeof ps === 'object') setPrivateShow(ps);
      pendingCommandPaywallRef.current = null;
      setPendingCommandPaywall(null);
      try { controllerRef.current?.closePaywall(); } catch {}
      return;
    }

    const paywallData = {
      reason: (reason === 'premium_required' ? 'premium_required' : 'insufficient_credits') as 'insufficient_credits' | 'premium_required',
      neededCredits: Number.isFinite(needed) ? Math.max(0, needed) : 0,
      balance: (paywall as any).balance ?? null,
      commandName: cmdName || 'action',
    };
    pendingCommandPaywallRef.current = paywallData;
    ctaDismissedRef.current = false; // reset dismiss flag for new paywall trigger
    setPendingCommandPaywall(paywallData);

    // Close legacy paywall state so it doesn't keep retriggering (we'll open later).
    try { controllerRef.current?.closePaywall(); } catch {}
  }, [paywall?.open]);
  const currentSrc = videoARef.current?.currentSrc || videoARef.current?.src || '';
  const isMseLikely = typeof currentSrc === 'string' && currentSrc.startsWith('blob:');
  const availableCount = commands.reduce((acc, c) => acc + (c.available ? 1 : 0), 0);
  const commandsUi = useMemo(() => {
    // Show only available commands, hide unavailable
    return commands.filter((c) => c.available);
  }, [commands]);

  // Check crowdfund status when model loads and has no commands
  useEffect(() => {
    if (!playerReady) return;
    const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
    if (!modelIdNum || modelIdNum <= 0) return;
    // Only check once per model
    if (crowdfundCheckedRef.current === modelIdNum) return;
    crowdfundCheckedRef.current = modelIdNum;
    
    // Fetch crowdfund status
    (async () => {
      try {
        const apiBase = services.config.config.apiBaseUrl || '';
        const response = await fetch(`${apiBase}/api/model/${modelIdNum}/crowdfund`);
        if (!response.ok) return;
        const data = await response.json();
        setCrowdfundEnabled(data?.enabled === true);
      } catch (err) {
        console.warn('[ChatRoute] Failed to check crowdfund status:', err);
      }
    })();
  }, [playerReady, playerState?.modelId, route.name, route.name === 'chat' ? route.modelId : '']);

  // Show crowdfund panel when enabled and no commands available
  const showCrowdfundPanel = crowdfundEnabled && commandsUi.length === 0;

  const purchasePrivateShow = useCallback(async () => {
    if (privateShowPurchasing) return;
    const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
    const chatThreadId = String(chatThreadIdRef.current || '').trim();
    const chatSessionId = String(chatSessionIdRef.current || '').trim();
    if (!Number.isFinite(modelIdNum) || modelIdNum <= 0 || !chatThreadId) return;
    if (!privateShowIdempotencyRef.current) {
      const rand = globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      privateShowIdempotencyRef.current = `miniapp:${modelIdNum}:${chatThreadId}:${rand}`;
    }
    setPrivateShowPurchasing(true);
    try {
      trackEvent('private_show_purchase_started', {
        modelId: modelIdNum,
        modelName: playerState?.modelName,
        chatSessionId: chatSessionId || undefined,
        origin: 'chat',
        metadata: {
          chat_thread_id: chatThreadId,
          price_credits: privateShow?.price_credits ?? null,
        },
      });
    } catch {}
    try {
      const r = await authedFetchJson<any>('/api/private-show/purchase', {
        method: 'POST',
        body: JSON.stringify({
          modelId: modelIdNum,
          chatThreadId,
          chatSessionId: chatSessionId || null,
          idempotencyKey: privateShowIdempotencyRef.current,
          source: 'miniapp_chat',
        }),
      });
      if (!r.ok) {
        const data = (r.data || {}) as any;
        try {
          trackEvent('private_show_purchase_failed', {
            modelId: modelIdNum,
            modelName: playerState?.modelName,
            chatSessionId: chatSessionId || undefined,
            origin: 'chat',
            metadata: {
              reason: String(data?.error || data?.code || 'purchase_failed'),
              required_credits: data?.required_credits ?? null,
              balance: data?.balance ?? null,
            },
          });
        } catch {}
        if (r.status === 402) {
          const neededCredits = Math.max(0, Number(data?.required_credits || privateShow?.price_credits || 0) || 0);
          const balance = data?.balance === null || data?.balance === undefined ? null : Number(data.balance);
          const ctx: TopupSheetContext = {
            origin: 'chat',
            modelId: modelIdNum,
            modelName: playerState?.modelName || undefined,
            modelAvatarUrl: playerState?.avatarUrl || undefined,
            chatSessionId,
            neededCredits,
            commandName: t('store.private_show', 'Private show'),
            balance,
          };
          try { props.onOpenTopup?.(ctx); } catch {}
          try { window.dispatchEvent(new CustomEvent('aicams-open-topup-sheet', { detail: ctx })); } catch {}
        }
        return;
      }
      const data = (r.data || {}) as any;
      const nextState = (data.private_show && typeof data.private_show === 'object') ? data.private_show : null;
      if (nextState) setPrivateShow(nextState);
      const purchase = (data.purchase && typeof data.purchase === 'object') ? data.purchase : {};
      const access = (data.access && typeof data.access === 'object') ? data.access : {};
      try {
        trackEvent('private_show_purchase_succeeded', {
          modelId: modelIdNum,
          modelName: playerState?.modelName,
          chatSessionId: chatSessionId || undefined,
          origin: 'chat',
          metadata: {
            chat_thread_id: chatThreadId,
            grant_id: String(access?.grant_id || nextState?.grant_id || ''),
            charged: purchase?.charged === true,
            price_credits: purchase?.amount_credits ?? nextState?.price_credits ?? privateShow?.price_credits ?? null,
          },
        });
      } catch {}
    } finally {
      setPrivateShowPurchasing(false);
    }
  }, [
    privateShowPurchasing,
    playerState?.modelId,
    playerState?.modelName,
    playerState?.avatarUrl,
    privateShow?.price_credits,
    route.name,
    route.name === 'chat' ? route.modelId : '',
    props.onOpenTopup,
    t,
  ]);

  // Legacy-like UX: after state changes (typically after transition), scroll to first active command.
  useEffect(() => {
    const stateId = playerState?.currentStateId ?? null;
    const container = commandsRef.current;
    if (!container || !stateId) return;
    const prev = lastStateIdRef.current;
    lastStateIdRef.current = stateId;
    if (prev === null) return; // initial mount
    if (prev === stateId) return;

    const tId = window.setTimeout(() => {
      const firstActive = container.querySelector('.nsfw-cmd.available') as HTMLElement | null;
      if (!firstActive) return;
      container.scrollTo({
        left: Math.max(0, firstActive.offsetLeft - 16),
        behavior: 'smooth',
      });
    }, 80);
    return () => window.clearTimeout(tId);
  }, [playerState?.currentStateId]);

  const handleSend = async (text: string) => {
    const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
    const chatThreadId = String(chatThreadIdRef.current || '').trim();
    const chatSessionId = String(chatSessionIdRef.current || '').trim();
    if (!text.trim()) return;
    if (!Number.isFinite(modelIdNum) || modelIdNum <= 0) return;

    if (!chatThreadId || !chatSessionId) return;
    if (privateShowGateOpen) return;

    const clientMessageId = globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const clientRequestId = clientMessageId;

    const userMsg: ChatMessage = {
      id: clientMessageId,
      role: 'user',
      content: text,
      timestamp: Date.now(),
    };
    const assistantMsgId = `run_${clientRequestId}`;
    pendingByRequestRef.current.set(clientRequestId, assistantMsgId);
    // Hide CTA when user sends a new message
    setActionCta(null);
    setMessages(prev => {
      const ids = new Set(prev.map(m => String(m.id)));
      const next = prev.slice();
      if (!ids.has(userMsg.id)) next.push(userMsg);
      if (!ids.has(assistantMsgId)) next.push({ id: assistantMsgId, role: 'model', content: '', timestamp: Date.now(), clientRequestId } as ChatMessage);
      return next;
    });
    setRelationshipLevel(prev => Math.min(100, prev + 2));

    // Increment messages counter for surprise offers
    setMessagesSent(prev => prev + 1);

    // Show credit deduction animation for FREE users (only when status is confirmed)
    try {
      const rStart = await authedFetchJson<{ runId: string; charged?: boolean; included?: boolean; access_source?: string | null; private_show?: any; private_show_grant_id?: string | null; insufficient_credits?: boolean; message_cost_credits?: number; listed_message_cost_credits?: number; required?: number; balance?: number | null }>(
        '/api/chat/message/start',
        {
          method: 'POST',
          body: JSON.stringify({
            chatThreadId,
            chatSessionId,
            modelId: modelIdNum,
            message: text,
            client_message_id: clientMessageId,
            client_request_id: clientRequestId,
          }),
        }
      );
      if (!rStart.ok) {
        // Preserve original AppError (status/resp/body_snippet) for telemetry/debugging.
        const err0: any = (rStart as any).err || null;
        const data0: any = (rStart as any).data || {};
        // Best-effort: remove placeholder so dots don't hang forever.
        try {
          pendingByRequestRef.current.delete(clientRequestId);
          setMessages((prev) => prev.filter((m) => String(m.id) !== String(assistantMsgId)));
        } catch {}
        if (String(data0?.error || '') === 'private_show_required') {
          if (data0.private_show && typeof data0.private_show === 'object') setPrivateShow(data0.private_show);
          return;
        }
        const isHardPaywall = data0?.hard_cap === true || String(data0?.error || '') === 'insufficient_credits_hard_cap';
        if (isHardPaywall) {
          const cmdName = t('chat.message', 'Message');
          const neededCredits = Math.max(0, Number(data0?.required ?? messageCostCredits) || 0);
          const balance = data0?.balance ?? null;
          const freeCreditsEnabled = (props as any).freeCreditsPurchaseEnabled === true;
          const shouldPremium = isPremium !== true && freeCreditsEnabled !== true;
          ctaDismissedRef.current = false;
          if (shouldPremium && typeof (props as any).onOpenPremium === 'function') {
            (props as any).onOpenPremium({ context: { origin: 'chat', commandName: cmdName } });
            window.dispatchEvent(new CustomEvent('aicams-open-premium-sheet', { detail: { context: { origin: 'chat', commandName: cmdName } } }));
          } else {
            setActionCta({
              kind: 'topup',
              track: { reason: 'insufficient_credits', neededCredits, balance, commandName: cmdName },
              topupCtx: {
                origin: 'chat',
                modelId: modelIdNum,
                modelName: playerState?.modelName || undefined,
                modelAvatarUrl: playerState?.avatarUrl || undefined,
                chatSessionId,
                neededCredits,
                commandName: cmdName,
                balance,
              },
            });
          }
          return;
        }
        // FREE cannot buy credits; if blocked by insufficient credits, upsell Premium.
        try {
          if (isPremium === false && String((rStart.data as any)?.error || '').includes('insufficient')) {
            const cmdName = t('chat.message', 'Message');
            (props as any).onOpenPremium?.({ context: { origin: 'chat', commandName: cmdName } });
            window.dispatchEvent(new CustomEvent('aicams-open-premium-sheet', { detail: { context: { origin: 'chat', commandName: cmdName } } }));
          }
        } catch {}
        // Also report as handled error with full context (best-effort; no UI spam).
        try {
          reportHandledError({
            err: err0 || new Error(String((rStart.data as any)?.error || 'start_failed')),
            kind: 'api',
            code: 'chat.message_start_failed',
            userMessage: '',
            severity: 'error',
            context: {
              feature: 'chat',
              stage: 'message_start',
              endpoint: '/api/chat/message/start',
              modelId: Number(modelIdNum || 0) || null,
              chatSessionId: chatSessionIdRef.current || null,
              chatThreadId: chatThreadIdRef.current || null,
            },
          });
        } catch {}
        return;
      }

      try {
        const ps = (rStart.data as any)?.private_show;
        if (ps && typeof ps === 'object') setPrivateShow(ps);
        if ((rStart.data as any)?.included === true && (rStart.data as any)?.access_source === 'private_show_access') {
          trackEvent('private_show_access_used', {
            modelId: modelIdNum,
            modelName: playerState?.modelName,
            chatSessionId,
            origin: 'chat',
            metadata: {
              action: 'chat_message',
              chat_thread_id: chatThreadId,
              private_show_grant_id: (rStart.data as any)?.private_show_grant_id || null,
              credits_cost: Math.max(0, Number((rStart.data as any)?.message_cost_credits ?? privateShowMessageCostCredits) || 0),
            },
          });
        }
      } catch {}

      // If backend flagged insufficient credits, defer CTA until model finishes typing.
      try {
        const insuff = Boolean((rStart.data as any)?.insufficient_credits);
        const shouldPend = Boolean(insuff);
        pendingInsufficientRef.current = shouldPend;
        pendingMessagePaywallRef.current = shouldPend ? {
          neededCredits: Math.max(0, Number((rStart.data as any)?.required ?? (rStart.data as any)?.listed_message_cost_credits ?? messageCostCredits) || 0),
          balance: (rStart.data as any)?.balance ?? null,
        } : null;
        if (shouldPend) ctaDismissedRef.current = false; // reset dismiss flag for new paywall trigger
        setPendingInsufficient(shouldPend);
      } catch {}

      const runId = String((rStart.data as any)?.runId || '').trim();
      if (!runId) return;

      // Track quest progress for chat messages (best-effort, fire-and-forget)
      void authedFetchJsonUtil(services, '/api/quests/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_type: 'chat_messages', delta: 1 }),
      }).catch(() => {});

      await streamRunToMessage(runId, assistantMsgId, { cancelPrevious: true });
    } catch (e) {
      try {
        reportHandledError({
          err: e,
          kind: 'api',
          code: 'chat.message_send_failed',
          userMessage: '',
          severity: 'error',
          context: {
            feature: 'chat',
            stage: 'message_send',
            modelId: Number(modelIdNum || 0) || null,
            chatSessionId: chatSessionIdRef.current || null,
            chatThreadId: chatThreadIdRef.current || null,
          },
        });
      } catch {}
    }
  };

  function normalizeAssistantText(raw: string): string {
    const s = String(raw || '').trim();
    if (!s) return '';
    // If backend accidentally streamed JSON, extract assistant_message.
    // Keep this defensive: only parse when it clearly looks like our JSON contract.
    if ((s.startsWith('{') || s.startsWith('```')) && (s.includes('assistant_message') || s.includes('assistantMessage'))) {
      try {
        let t0 = s;
        if (t0.startsWith('```')) {
          const nl = t0.indexOf('\n');
          if (nl >= 0) t0 = t0.slice(nl + 1);
          const end = t0.lastIndexOf('```');
          if (end >= 0) t0 = t0.slice(0, end);
          t0 = t0.trim();
        }
        const obj: any = JSON.parse(t0);
        const msg = String(obj?.assistant_message || obj?.assistantMessage || '').trim();
        if (msg) return msg;
      } catch {}
    }
    return s;
  }

  // Reliable delivery WS (B1): subscribe to assistant messages in this thread.
  useEffect(() => {
    if (route.name !== 'chat') return;
    if (!playerReady) return;
    const threadId = String(chatThreadIdRef.current || '').trim();
    const chatSessionId = String(chatSessionIdRef.current || '').trim();
    if (!threadId) return;
    if (!chatSessionId) return;
    let cancelled = false;

    const closeWs = () => {
      try { if (chatWsReconnectRef.current) window.clearTimeout(chatWsReconnectRef.current); } catch {}
      chatWsReconnectRef.current = null;
      try { chatWsRef.current?.close(); } catch {}
      chatWsRef.current = null;
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      try { if (chatWsReconnectRef.current) window.clearTimeout(chatWsReconnectRef.current); } catch {}
      chatWsReconnectRef.current = window.setTimeout(() => void connect(), 1200);
    };

    const connect = async () => {
      if (cancelled) return;
      if (chatWsRef.current && (chatWsRef.current.readyState === WebSocket.OPEN || chatWsRef.current.readyState === WebSocket.CONNECTING)) return;
      const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 5000 });
      const accessToken = hasJwt ? services.supabase.getAccessToken() : null;
      if (!accessToken) return;
      const apiBaseUrl = services.config.config.apiBaseUrl;
      let wsUrl = '';
      try {
        const u = new URL(apiBaseUrl);
        u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
        u.pathname = '/api/chat/ws';
        // IMPORTANT (security): do NOT put bearer tokens in URL query (RFC 6750 §2.3 warns URLs get logged).
        // We send the token via Sec-WebSocket-Protocol instead.
        u.search = '';
        wsUrl = u.toString();
      } catch {
        return;
      }

      // Browser WebSocket doesn't allow custom headers; use subprotocol to convey auth token.
      // Server expects: Sec-WebSocket-Protocol: aicams.jwt, <jwt>
      const ws = new WebSocket(wsUrl, ['aicams.jwt', accessToken]);
      chatWsRef.current = ws;

      ws.onopen = () => {
        try {
          const cursor = wsCursorRef.current ? { ...wsCursorRef.current } : undefined;
          ws.send(JSON.stringify({
            type: 'chat.subscribe',
            chatThreadId: threadId,
            chatSessionId,
            ...(cursor ? { cursor } : {}),
          }));
          // Telemetry (best-effort): WS opened + subscribe sent
          try {
            captureError({
              type: 'chat.ws_open',
              message: 'ws_open',
              severity: 'info',
              chatSessionId: chatSessionIdRef.current || null,
              meta: {
                incident_id: `chat:${chatSessionIdRef.current || 'unknown'}`,
                feature: 'chat',
                stage: 'ws_open',
                chatThreadId: threadId,
                cursor_sent: cursor || null,
              },
            });
          } catch {}
        } catch {}
      };
      ws.onclose = (ev: any) => {
        if (chatWsRef.current === ws) chatWsRef.current = null;
        // Telemetry (best-effort): close reasons (useful to debug 499/upgrade issues)
        try {
          captureError({
            type: 'chat.ws_close',
            message: 'ws_close',
            severity: 'info',
            chatSessionId: chatSessionIdRef.current || null,
            meta: {
              incident_id: `chat:${chatSessionIdRef.current || 'unknown'}`,
              feature: 'chat',
              stage: 'ws_close',
              code: (ev && typeof ev.code === 'number') ? ev.code : null,
              reason: ev?.reason ? String(ev.reason).slice(0, 200) : null,
              wasClean: (ev && typeof ev.wasClean === 'boolean') ? ev.wasClean : null,
            },
          });
        } catch {}
        scheduleReconnect();
      };
      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
      ws.onmessage = (e) => {
        try {
          const data = typeof e.data === 'string' ? e.data : '';
          const j = data ? JSON.parse(data) : null;
          const type = String(j?.type || '');
          if (type === 'chat.snapshot' && String(j?.chatThreadId || '') === threadId) {
            const arr = Array.isArray(j?.messages) ? j.messages : [];
            try {
              const nc = j?.nextCursor || null;
              const createdAt = String(nc?.createdAt ?? nc?.created_at ?? '').trim();
              const id = String(nc?.id ?? '').trim();
              if (createdAt) wsCursorRef.current = { createdAt, ...(id ? { id } : {}) };
            } catch {}
            // Ack cursor (throttled) for observability + future resume guarantees.
            try {
              const cur = wsCursorRef.current;
              const now = Date.now();
              if (cur && now - chatWsLastAckAtRef.current > 1500) {
                chatWsLastAckAtRef.current = now;
                ws.send(JSON.stringify({ type: 'chat.ack', cursor: cur }));
              }
            } catch {}
            // Telemetry (best-effort): snapshot empty with non-empty cursor is useful to debug cursor bugs.
            try {
              if (!arr.length && wsCursorRef.current?.createdAt) {
                captureError({
                  type: 'chat.ws_snapshot_empty',
                  message: 'ws_snapshot_empty',
                  severity: 'info',
                  chatSessionId: chatSessionIdRef.current || null,
                  meta: {
                    incident_id: `chat:${chatSessionIdRef.current || 'unknown'}`,
                    feature: 'chat',
                    stage: 'ws_snapshot',
                    chatThreadId: threadId,
                    cursor: wsCursorRef.current,
                  },
                });
              }
            } catch {}
            if (!arr.length) return;
            setMessages((prev) => mergeAssistantFromWs(prev, arr));
            return;
          }
          if (type === 'chat.message' && String(j?.chatThreadId || '') === threadId) {
            const m = j?.message;
            try {
              const c = j?.cursor || null;
              const createdAt = String(c?.createdAt ?? c?.created_at ?? m?.created_at ?? '').trim();
              const id = String(c?.id ?? m?.id ?? '').trim();
              if (createdAt) wsCursorRef.current = { createdAt, ...(id ? { id } : {}) };
            } catch {}
            // Ack cursor (throttled)
            try {
              const cur = wsCursorRef.current;
              const now = Date.now();
              if (cur && now - chatWsLastAckAtRef.current > 1500) {
                chatWsLastAckAtRef.current = now;
                ws.send(JSON.stringify({ type: 'chat.ack', cursor: cur }));
              }
            } catch {}
            setMessages((prev) => mergeAssistantFromWs(prev, [m]));
            return;
          }
        } catch {}
      };
    };

    const mergeAssistantFromWs = (prev: ChatMessage[], rows: any[]) => {
      const next = prev.slice();
      const byServerId = new Map<string, number>();
      for (let i = 0; i < next.length; i++) {
        const sid = String((next[i] as any).serverId || '');
        if (sid) byServerId.set(sid, i);
      }
      for (const r of rows) {
        const serverId = String(r?.id || '').trim();
        if (!serverId) continue;
        const content = String(r?.content || '').trim();
        const clientRequestId = String(r?.client_request_id || '').trim();
        const ts = r?.created_at ? Date.parse(String(r.created_at)) : Date.now();
        const contentJson = r?.content_json && typeof r.content_json === 'object' ? r.content_json : {};

        // If we already have this server message, just ensure content is filled.
        const exIdx = byServerId.get(serverId);
        if (typeof exIdx === 'number') {
          const cur = next[exIdx];
          if (!cur.content && content) next[exIdx] = { ...cur, content, contentJson };
          else next[exIdx] = { ...cur, contentJson: { ...((cur as any).contentJson || {}), ...contentJson } };
          continue;
        }

        // Try to resolve a pending placeholder by clientRequestId.
        const pendingId = clientRequestId ? pendingByRequestRef.current.get(clientRequestId) : null;
        if (pendingId) {
          const idx = next.findIndex((m) => String(m.id) === String(pendingId));
          if (idx >= 0) {
            next[idx] = {
              ...next[idx],
              serverId,
              id: next[idx].id, // keep UI id stable
              content: content || next[idx].content,
              contentJson,
              timestamp: Number.isFinite(ts) ? ts : next[idx].timestamp,
              clientRequestId,
            };
            byServerId.set(serverId, idx);
            if (content) pendingByRequestRef.current.delete(clientRequestId);
            // If committed arrived, we don't need to keep streaming this placeholder.
            try {
              if (activeStreamRef.current?.assistantMsgId && String(activeStreamRef.current.assistantMsgId) === String(pendingId)) {
                cancelActiveStream({ reason: 'committed_arrived', removePlaceholder: false });
              }
            } catch {}
            continue;
          }
        }

        // Append as a normal model message
        next.push({
          id: `srv_${serverId}`,
          serverId,
          clientRequestId,
          role: 'model',
          content,
          contentJson,
          timestamp: Number.isFinite(ts) ? ts : Date.now(),
        } as any);
        byServerId.set(serverId, next.length - 1);
      }
      next.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
      return next;
    };

    void connect();
    return () => closeWs();
  }, [route.name, playerReady, route.name === 'chat' ? route.modelId : '']);

  const cancelActiveStream = (opts?: { reason?: string; removePlaceholder?: boolean }) => {
    const cur = activeStreamRef.current;
    if (!cur) return;
    activeStreamRef.current = null;
    try { cur.abort.abort(); } catch {}
    const remove = (opts?.removePlaceholder !== false);
    if (remove) {
      try {
        setMessages((prev) => {
          const m = prev.find((x) => String(x.id) === String(cur.assistantMsgId));
          if (!m) return prev;
          // Only remove empty placeholders; never delete real content.
          if (String((m as any).content || '').trim()) return prev;
          return prev.filter((x) => String(x.id) !== String(cur.assistantMsgId));
        });
      } catch {}
    }
  };


  const streamRunToMessage = async (runId: string, assistantMsgId: string, opts?: { cancelPrevious?: boolean }) => {
    const rid = String(runId || '').trim();
    if (!rid) return;
    if (opts?.cancelPrevious) {
      // Latest-wins: cancel previous in-flight run stream to avoid “stuck dots”.
      cancelActiveStream({ reason: 'latest_wins', removePlaceholder: true });
    }
    const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 5000 });
    const accessToken = hasJwt ? services.supabase.getAccessToken() : null;
    if (!accessToken) {
      // Avoid leaving an empty typing placeholder behind if we can't stream.
      try {
        setMessages((prev) => {
          const m = prev.find((x) => String(x.id) === String(assistantMsgId));
          if (!m) return prev;
          if (String((m as any).content || '').trim()) return prev;
          return prev.filter((x) => String(x.id) !== String(assistantMsgId));
        });
      } catch {}
      return;
    }
    const apiBaseUrl = services.config.config.apiBaseUrl;
    const abort = new AbortController();
    activeStreamRef.current = { runId: rid, assistantMsgId: String(assistantMsgId), abort, startedAt: Date.now(), hasContent: false };
    let watchdog: any = null;
    try {
      // If we still have an empty placeholder after a while, remove it (covers cancelled runs / broken streams).
      watchdog = window.setTimeout(() => {
        const cur = activeStreamRef.current;
        if (!cur) return;
        if (cur.runId !== rid) return;
        // Only nuke truly empty placeholders; never kill in-progress text.
        if (cur.hasContent) return;
        cancelActiveStream({ reason: 'watchdog', removePlaceholder: true });
      }, 14_000);
    } catch {}

    let res: Response | null = null;
    let assembled = '';
    try {
      res = await apiFetch(`${apiBaseUrl}/api/chat/run/${encodeURIComponent(rid)}/stream`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: abort.signal,
      });
      if (!res.ok || !res.body) {
        // Best-effort: if we failed to start streaming, remove empty placeholder.
        cancelActiveStream({ reason: 'stream_start_failed', removePlaceholder: true });
        return;
      }

      const reader = (res.body as any).getReader ? (res.body as any).getReader() : null;
      const decoder = new TextDecoder();
      let buf = '';
      while (reader) {
        const { done, value } = await reader.read().catch((e: any) => {
          const aborted = String(e?.name || '').toLowerCase().includes('abort');
          if (aborted) return { done: true, value: null as any };
          throw e;
        });
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 1);
          if (!line) continue;
          let obj: any = null;
          try { obj = JSON.parse(line); } catch { obj = null; }
          if (!obj) continue;
          const ev = obj?.type === 'event' ? obj.event : null;
          const innerType = String(ev?.type || obj?.type || '');
          if (innerType === 'assistant_text_delta') {
            const delta = String(ev?.delta || '');
            const isFinal = ev?.final === true;
            assembled = isFinal ? normalizeAssistantText(delta) : (assembled + delta);
            if (activeStreamRef.current?.runId === rid) activeStreamRef.current.hasContent = Boolean(assembled.trim());
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantMsgId) return m;
              // Invariant: committed (WS/DB) always wins. Once message is committed (serverId assigned),
              // stream must not overwrite it (prevents race-induced flips).
              if ((m as any)?.serverId) {
                try {
                  // Best-effort telemetry once per run
                  if (suppressStreamUpdateByRunRef.current !== rid) {
                    suppressStreamUpdateByRunRef.current = rid;
                    captureError({
                      type: 'chat.stream_ignored',
                      message: 'stream_update_ignored_due_to_committed',
                      severity: 'info',
                      chatSessionId: chatSessionIdRef.current || null,
                      meta: {
                        incident_id: `chat:${chatSessionIdRef.current || 'unknown'}`,
                        feature: 'chat',
                        stage: 'stream_delta',
                        runId: rid,
                        assistantMsgId: String(assistantMsgId),
                        chatThreadId: chatThreadIdRef.current || null,
                      },
                    });
                  }
                } catch {}
                return m;
              }
              return { ...m, content: assembled };
            }));
          }
          if (obj?.type === 'end') break;
        }
      }

      if (assembled.trim()) setRelationshipLevel(prev => Math.min(100, prev + 3));
      // If stream ended with no content, remove placeholder (likely cancelled).
      if (!assembled.trim()) {
        try {
          setMessages((prev) => prev.filter((m) => String(m.id) !== String(assistantMsgId)));
        } catch {}
      }
    } catch (e: any) {
      const aborted = String(e?.name || '').toLowerCase().includes('abort');
      if (aborted) return;
      throw e;
    } finally {
      // Clear active stream if it’s still ours.
      if (activeStreamRef.current?.runId === rid) {
        activeStreamRef.current = null;
      }
      try { if (watchdog) window.clearTimeout(watchdog); } catch {}
    }
  };

  const handleCommandClick = async (ev: MouseEvent, c: any) => {
    if (!c.available) return;

    // Safety: ignore synthetic/untrusted clicks (prevents “paywall without taps”)
    try {
      if ((ev as any)?.isTrusted === false) return;
    } catch {}

    // Anti click-through: ignore any command clicks right after entering chat.
    // This prevents “tap model card -> click lands on first command button”.
    if (Date.now() - enteredAtRef.current < 1100) return;

    // Never open paywall / execute commands until player is ready.
    if (!playerReady) return;
    // Hard guard: if player is busy, ignore click.
    if (actionsLocked) return;
    // Sync guard: if we already accepted a click this tick, ignore.
    if (commandClickLockRef.current) return;
    commandClickLockRef.current = true;
    
    // Add system message
    const content = translateCommandText(c.command, t, locale);
    const msgId = globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const assistantMsgId = `run_${msgId}`;
    setMessages(prev => [...prev, {
      id: msgId,
      role: 'system',
      content,
      timestamp: Date.now()
    }]);
    
    // Commands boost level significantly
    setRelationshipLevel(prev => Math.min(100, prev + 10));
    
    // Increment commands counter for surprise offers
    setCommandsExecuted(prev => prev + 1);

    // Execute command (billing + video). Start streaming as soon as backend returns runId.
    try {
      let started = false;
      const onRunId = (rid: string) => {
        if (started) return;
        started = true;
        // Show typing ONLY after we have a real run id.
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => String(m.id)));
          const next = prev.slice();
          if (!ids.has(assistantMsgId)) next.push({ id: assistantMsgId, role: 'model', content: '', timestamp: Date.now() } as any);
          return next;
        });
        void streamRunToMessage(rid, assistantMsgId, { cancelPrevious: true }).catch(() => {});
      };
      const rExec: ExecuteResult | null = await controllerRef.current?.execute(c, {
        commandEventId: msgId,
        displayText: content,
        uiLocale: services.i18n.getLocale(),
        onRunId,
      }) as any;

      if (rExec && typeof rExec === 'object' && (rExec as any).status === 'ok' && privateShowIncluded) {
        try {
          trackEvent('private_show_access_used', {
            modelId: Number(playerState?.modelId || 0) || undefined,
            modelName: playerState?.modelName,
            chatSessionId: chatSessionIdRef.current || undefined,
            origin: 'chat',
            metadata: {
              action: String(c?.kind || 'command'),
              command: String(c?.command || ''),
              chat_thread_id: chatThreadIdRef.current || null,
              private_show_grant_id: (rExec as any)?.privateShowGrantId || privateShow?.grant_id || null,
              credits_cost: Math.max(0, Number((rExec as any)?.spentCost ?? privateShowCommandCostCredits) || 0),
            },
          });
        } catch {}
      }

      // If player attempted the action and failed, report it (handled error; no UI popup).
      if (rExec && typeof rExec === 'object' && (rExec as any).status === 'error') {
        const cause = (rExec as any)?.cause ?? new Error(String((rExec as any).err || 'execute_failed'));
        const meta = (rExec as any)?.meta && typeof (rExec as any).meta === 'object' ? (rExec as any).meta : null;
        reportHandledError({
          err: cause,
          kind: 'player',
          code: 'chat.command_execute_failed',
          userMessage: '',
          severity: 'error',
          context: {
            feature: 'chat',
            stage: 'execute',
            command: String(c?.command || ''),
            kind: String(c?.kind || ''),
            modelId: Number(playerState?.modelId || 0) || null,
            chatSessionId: chatSessionIdRef.current || null,
            chatThreadId: chatThreadIdRef.current || null,
            stateId: playerState?.currentStateId || null,
            exec_err: String((rExec as any).err || 'execute_failed'),
            exec_meta: meta,
          },
        });
      }
    } catch (e) {
      console.warn('handleCommandClick failed:', e);
      reportHandledError({
        err: e,
        kind: 'api',
        code: 'chat.command_click_failed',
        userMessage: '',
        severity: 'error',
        context: {
          feature: 'chat',
          stage: 'command_click',
          command: String(c?.command || ''),
          kind: String(c?.kind || ''),
          modelId: Number(playerState?.modelId || 0) || null,
          chatSessionId: chatSessionIdRef.current || null,
          chatThreadId: chatThreadIdRef.current || null,
          stateId: playerState?.currentStateId || null,
        },
      });
    } finally {
      // Keep the lock while a transition/once is running; release immediately if not busy.
      try {
        const busyNow = Boolean(controllerRef.current?.getState?.()?.isBusy);
        // Also keep lock while we are waiting to show/paywall CTA.
        const stillLocked = Boolean(busyNow || actionCta || pendingInsufficient || pendingCommandPaywall || privateShowGateOpen || privateShowPurchasing);
        if (!stillLocked) commandClickLockRef.current = false;
      } catch {
        commandClickLockRef.current = false;
      }
    }
  };

  return (
    <div class="chatRoot chat-page">
      <div ref={shellRef} class="chat-fullscreen">
        <div ref={mediaRef} class="chat-media" id="mediaContainer">
          <video ref={videoARef} class="model-fullscreen-video" muted playsInline preload="auto" style="z-index:2;" />
          <video ref={videoBRef} class="model-fullscreen-video" muted playsInline preload="auto" style="z-index:1;" />
          <div class="media-gradient-top"></div>
          <div class="media-gradient-bottom"></div>
          {debugEnabled ? (
            <div class="chat-video-debug" aria-hidden={!debugEnabled}>
              <div
                class="chat-video-debugBadge"
                title={`MSE=${isMseLikely ? 'yes' : 'no'} state=${playerState?.currentStateId || '—'} cmds=${availableCount}/${commands.length}${initError ? ` err=${initError}` : ''}`}
              >
                MSE:{isMseLikely ? 'Y' : 'N'} · {playerState?.currentStateId || '—'} · {availableCount}/{commands.length}
                {initError ? ' · ERR' : ''}
              </div>
              <button
                class="iconBtn nsfw-btn-icon chat-video-debugBtn"
                onClick={onToggleDebug}
                title={t('debug.player_title', 'Player Debug')}
              >
                <IconGear />
              </button>
            </div>
          ) : null}

          {playerState?.isResetting ? (
            <div class="chatResetOverlay" aria-hidden={!playerState?.isResetting}>
              <div class="chatResetSpinner" aria-label={t('common.loading', 'Loading')} />
            </div>
          ) : null}
          
          {/* Surprise Offer Overlay (hidden for Premium when benefit is enabled) */}
          {surpriseOffer.visible && surpriseOffer.offer &&
            !(isPremium === true && premiumBenefits?.hide_surprise_offers) && (
            <SurpriseOfferOverlay
              offer={surpriseOffer.offer}
              onClose={surpriseOffer.close}
              onCtaClick={surpriseOffer.handleCtaClick}
            />
          )}

          {/* Golden Button (Get Free Spins) */}
          {playerReady && playerState?.modelId && (
            <GoldenButton modelId={playerState.modelId} />
          )}
        </div>

        <div class="chat-top-controls" id="chatTopControls">
          {/* Left Column: Reset Button */}
          <div class="chat-ctrl-left">
            <button 
              class="iconBtn chat-reset-btn" 
              onClick={onReset} 
              title={t('btn.reset', 'Reset')}
              disabled={isBusy || !playerReady}
            >
              <IconRefresh size={18} />
            </button>
          </div>

          {/* Center Column: Badge */}
          <div class="chat-ctrl-center">
            <button
              type="button"
              class={`chat-model-badge ${privateBotUnlocked ? 'private-bot-unlocked' : ''} ${privateBotOpening ? 'is-opening' : ''}`}
              id="chatTopBar"
              onClick={privateBotUnlocked ? openPrivateBot : undefined}
              disabled={!privateBotUnlocked || privateBotOpening}
              title={privateBotUnlocked ? t('chat.private_bot_open', 'Open private Telegram') : undefined}
              aria-label={privateBotUnlocked ? t('chat.private_bot_open', 'Open private Telegram') : t('chat.model_fallback', 'Model')}
            >
              <span class="chat-badge-avatarShell" aria-hidden="true">
                {playerState?.avatarUrl ? (
                  <img class="chat-badge-avatar chat-badge-avatarImg" src={playerState.avatarUrl} alt="" />
                ) : (
                  <div class="chat-badge-avatar chat-badge-avatarPlaceholder" />
                )}
                <span class="chat-badge-avatarVeil" />
                <span class="chat-badge-avatarTelegram">
                  <IconTelegram size={24} />
                </span>
                <span class="chat-badge-avatarGlow" />
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', lineHeight: 1, minWidth: '6em' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}>
                  <span class="chat-badge-name">{playerState?.modelName || t('chat.model_fallback', 'Model')}</span>
                  <span class="chat-badge-live">
                    <span class="live-dot"></span>
                    <span>{t('chat.live', 'LIVE')}</span>
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <RelationshipBar 
                    level={relationshipLevel} 
                  />
                </div>
                {privateShowIncluded && privateShowMessageCostCredits <= 0 && privateShowCommandCostCredits <= 0 && (
                  <div style={{ marginTop: 4, fontSize: 10, fontWeight: 900, letterSpacing: 0, color: 'rgba(255,255,255,0.92)' }}>
                    {t('private_show.included', 'Included')}
                  </div>
                )}
              </div>
            </button>
            {privateBotError && <div class="chat-private-bot-error" role="alert">{privateBotError}</div>}
          </div>
        </div>

        <div class="chat-bottom-panel nsfw-panel chat-integrated-panel">
          <ChatMessageList
            messages={messages}
            onLastModelDone={() => {
              // If user dismissed CTA, don't show it again
              if (ctaDismissedRef.current) return;

              // Chat billing: show Premium CTA only after the model finished typing its reply.
              // Use ref to avoid closure issues
              if (pendingInsufficientRef.current) {
                pendingInsufficientRef.current = false;
                setPendingInsufficient(false);
                const cmdName = t('chat.message', 'Message');
                const msgPaywall = pendingMessagePaywallRef.current;
                pendingMessagePaywallRef.current = null;
                const neededCredits = Math.max(0, Number(msgPaywall?.neededCredits ?? messageCostCredits) || 0);
                const balance = msgPaywall?.balance ?? null;
                const freeCreditsEnabled = (props as any).freeCreditsPurchaseEnabled === true;
                const premiumFirst = (isPremium !== true && freeCreditsEnabled !== true);
                if (premiumFirst) {
                  setActionCta({
                    kind: 'premium',
                    track: { reason: 'insufficient_credits', neededCredits, balance, commandName: cmdName },
                    premiumCtx: { context: { origin: 'chat', commandName: cmdName, modelName: playerState?.modelName || undefined } },
                  });
                } else {
                  const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
                  setActionCta({
                    kind: 'topup',
                    track: { reason: 'insufficient_credits', neededCredits, balance, commandName: cmdName },
                    topupCtx: {
                      origin: 'chat',
                      modelId: modelIdNum,
                      modelName: playerState?.modelName || undefined,
                      modelAvatarUrl: playerState?.avatarUrl || undefined,
                      chatSessionId: chatSessionIdRef.current || null,
                      neededCredits,
                      commandName: cmdName,
                      balance,
                    },
                  });
                }
                return;
              }

              // Commands paywalls: show a CTA button (not the sheet) after model reply typing ends.
              // Use ref to avoid closure issues
              const p = pendingCommandPaywallRef.current;
              if (!p) return;
              pendingCommandPaywallRef.current = null;
              setPendingCommandPaywall(null);

              const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
              const chatSessionId = chatSessionIdRef.current || null;
              const modelName = playerState?.modelName || undefined;
              const modelAvatarUrl = playerState?.avatarUrl || undefined;

              const freeCreditsEnabled = (props as any).freeCreditsPurchaseEnabled === true;
              const premiumFirst = (isPremium !== true && freeCreditsEnabled !== true);

              // Decide which paywall should be opened by CTA click.
              const shouldPremium =
                p.reason === 'premium_required' ||
                (p.reason === 'insufficient_credits' && (isPremium !== true) && premiumFirst);

              if (shouldPremium) {
                setActionCta({
                  kind: 'premium',
                  track: { reason: p.reason, neededCredits: p.neededCredits, balance: p.balance ?? null, commandName: p.commandName },
                  premiumCtx: { context: { origin: 'chat', commandName: p.commandName || t('chat.message', 'Message'), modelName } },
                });
                return;
              }

              // Otherwise show Topup CTA (premium users, or free users when free credits purchase is enabled).
              setActionCta({
                kind: 'topup',
                track: { reason: p.reason, neededCredits: p.neededCredits, balance: p.balance ?? null, commandName: p.commandName },
                topupCtx: {
                  origin: 'chat',
                  modelId: modelIdNum,
                  modelName,
                  modelAvatarUrl,
                  chatSessionId,
                  neededCredits: p.neededCredits,
                  commandName: p.commandName || 'action',
                  balance: p.balance ?? null,
                },
              });
            }}
          />

          <div class="chat-actions-container">
            {privateShowGateOpen && (
              <div class="premium-cta-overlay">
                <div class="premium-cta-row">
                  <button
                    class="premium-gold-btn"
                    disabled={!playerReady || privateShowPurchasing}
                    onClick={() => void purchasePrivateShow()}
                  >
                    <span class="premium-btn-text">
                      {privateShowPurchasing
                        ? t('private_show.buying', 'Buying...')
                        : `${t('store.private_show', 'Private show')} · ${Number(privateShow?.price_credits || 0)}`
                      }
                    </span>
                    <IconStar class="premium-btn-icon" filled />
                  </button>
                </div>
              </div>
            )}

            {actionCta?.kind === 'premium' && typeof (props as any).onOpenPremium === 'function' && (
              <div class="premium-cta-overlay">
                <div class="premium-cta-row">
                  <button
                    class="premium-gold-btn"
                    disabled={!playerReady}
                    onClick={() => {
                      // Funnel: CTA click -> paywall open
                      try {
                        const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
                        const tr = actionCta?.track;
                        trackPaywallCtaClick({
                          origin: 'chat',
                          surface: 'chat_actions_overlay',
                          ctaKind: 'premium',
                          modelId: modelIdNum || undefined,
                          chatSessionId: chatSessionIdRef.current || null,
                          reason: tr?.reason,
                          neededCredits: tr?.neededCredits ?? null,
                          balance: tr?.balance ?? null,
                          commandName: tr?.commandName ?? null,
                        });
                      } catch {}
                      // Track paywall view on actual open (not on render)
                      try {
                        const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
                        const tr = actionCta?.track;
                        if (tr && modelIdNum) {
                          trackPaywallView({
                            modelId: modelIdNum,
                            chatSessionId: chatSessionIdRef.current || null,
                            neededCredits: tr.neededCredits,
                            balance: tr.balance,
                            origin: 'chat',
                            reason: tr.reason,
                          });
                        }
                      } catch {}
                      try {
                        const ctx = actionCta?.premiumCtx || { context: { origin: 'chat', commandName: t('chat.message', 'Message'), modelName: playerState?.modelName || undefined } };
                        (props as any).onOpenPremium?.(ctx);
                      } catch {}
                      try {
                        const ctx = actionCta?.premiumCtx || { context: { origin: 'chat', commandName: t('chat.message', 'Message'), modelName: playerState?.modelName || undefined } };
                        window.dispatchEvent(new CustomEvent('aicams-open-premium-sheet', { detail: ctx }));
                      } catch {}
                      setActionCta(null);
                    }}
                  >
                    <span class="premium-btn-text">{t('btn.premium', 'Get Premium')}</span>
                    <IconStar class="premium-btn-icon" filled />
                  </button>
                  <button
                    class="premium-cta-dismiss"
                    onClick={() => {
                      // Track CTA dismiss (same pattern as cta_click)
                      try {
                        const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
                        const tr = actionCta?.track;
                        trackPaywallCtaDismiss({
                          origin: 'chat',
                          surface: 'chat_actions_overlay',
                          ctaKind: 'premium',
                          modelId: modelIdNum || undefined,
                          chatSessionId: chatSessionIdRef.current || null,
                          reason: tr?.reason,
                          neededCredits: tr?.neededCredits ?? null,
                          balance: tr?.balance ?? null,
                          commandName: tr?.commandName ?? null,
                        });
                      } catch {}
                      // Clear CTA state and reset dedup key so next CTA view is tracked
                      ctaDismissedRef.current = true;
                      pendingInsufficientRef.current = false;
                      pendingMessagePaywallRef.current = null;
                      pendingCommandPaywallRef.current = null;
                      lastCtaViewKeyRef.current = '';
                      setActionCta(null);
                      setPendingInsufficient(false);
                      setPendingCommandPaywall(null);
                    }}
                    title={t('btn.close', 'Close')}
                    aria-label={t('btn.close', 'Close')}
                  />
                </div>
              </div>
            )}

            {actionCta?.kind === 'topup' && typeof props.onOpenTopup === 'function' && (
              <div class="premium-cta-overlay">
                <div class="premium-cta-row">
                  <button
                    class="premium-gold-btn topup"
                    disabled={!playerReady}
                    onClick={() => {
                      // Funnel: CTA click -> paywall open
                      try {
                        const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
                        const tr = actionCta?.track;
                        trackPaywallCtaClick({
                          origin: 'chat',
                          surface: 'chat_actions_overlay',
                          ctaKind: 'topup',
                          modelId: modelIdNum || undefined,
                          chatSessionId: chatSessionIdRef.current || null,
                          reason: tr?.reason,
                          neededCredits: tr?.neededCredits ?? null,
                          balance: tr?.balance ?? null,
                          commandName: tr?.commandName ?? null,
                        });
                      } catch {}
                      try {
                        const tr = actionCta?.track;
                        const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
                        if (tr && modelIdNum) {
                          trackPaywallView({
                            modelId: modelIdNum,
                            chatSessionId: chatSessionIdRef.current || null,
                            neededCredits: tr.neededCredits,
                            balance: tr.balance,
                            origin: 'chat',
                            reason: tr.reason,
                          });
                        }
                      } catch {}
                      try {
                        if (actionCta?.topupCtx) props.onOpenTopup?.(actionCta.topupCtx);
                      } catch {}
                      setActionCta(null);
                    }}
                  >
                    <span class="premium-btn-text">{t('btn.topup', 'Top up')}</span>
                    <IconStar class="premium-btn-icon" filled />
                  </button>
                  <button
                    class="premium-cta-dismiss"
                    onClick={() => {
                      // Track CTA dismiss (same pattern as cta_click)
                      try {
                        const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
                        const tr = actionCta?.track;
                        trackPaywallCtaDismiss({
                          origin: 'chat',
                          surface: 'chat_actions_overlay',
                          ctaKind: 'topup',
                          modelId: modelIdNum || undefined,
                          chatSessionId: chatSessionIdRef.current || null,
                          reason: tr?.reason,
                          neededCredits: tr?.neededCredits ?? null,
                          balance: tr?.balance ?? null,
                          commandName: tr?.commandName ?? null,
                        });
                      } catch {}
                      // Clear CTA state and reset dedup key so next CTA view is tracked
                      ctaDismissedRef.current = true;
                      pendingInsufficientRef.current = false;
                      pendingMessagePaywallRef.current = null;
                      pendingCommandPaywallRef.current = null;
                      lastCtaViewKeyRef.current = '';
                      setActionCta(null);
                      setPendingInsufficient(false);
                      setPendingCommandPaywall(null);
                    }}
                    title={t('btn.close', 'Close')}
                    aria-label={t('btn.close', 'Close')}
                  />
                </div>
              </div>
            )}

            <div 
              ref={commandsRef} 
              class={`nsfw-commands-grid ${actionCta ? 'commands-blurred' : ''}`}
            >
              {/* Crowdfund Panel - embedded in commands area when enabled */}
              {showCrowdfundPanel && (
                <CrowdfundPanel
                  modelId={Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0)}
                  modelName={playerState?.modelName || t('chat.model_fallback', 'Model')}
                  compact={true}
                  onNeedTopup={() => {
                    const modelIdNum = Number(playerState?.modelId || (route.name === 'chat' ? route.modelId : 0) || 0);
                    const chatSessionId = chatSessionIdRef.current || null;
                    const modelName = playerState?.modelName || undefined;
                    const modelAvatarUrl = playerState?.avatarUrl || undefined;
                    try { (props as any).onOpenTopup?.({ origin: 'crowdfund', modelId: modelIdNum, modelName, modelAvatarUrl, chatSessionId }); } catch {}
                    try { window.dispatchEvent(new CustomEvent('aicams-open-topup-sheet', { detail: { origin: 'crowdfund', modelId: modelIdNum, modelName, modelAvatarUrl, chatSessionId } })); } catch {}
                  }}
                />
              )}
              {/* Command buttons */}
              {commandsUi.map((c) => {
                const isModelOfDay = playerState?.isModelOfDay ?? false;
                const modCost = playerState?.modelOfDayCommandCost ?? null;
                const originalCost = c.cost;
                // Apply Model of the Day discount: never increase price
                const effectiveCost = isModelOfDay && modCost !== null && originalCost > modCost ? modCost : originalCost;
                const showDiscount = isModelOfDay && modCost !== null && originalCost > modCost;
                
                return (
                <button
                  key={`${c.kind}:${c.command}`}
                  class={`nsfw-cmd ${c.available ? 'available' : 'unavailable'} ${c.premiumOnly ? 'premium-only' : ''} ${isModelOfDay ? 'model-of-day' : ''}`}
                  disabled={actionsLocked || !playerReady || !c.available}
                  onClick={(e) => handleCommandClick(e as any, c)}
                >
                  <span class="nsfw-cmd-label">
                    {translateCommandText(c.command, t, locale)}
                    {c.premiumOnly && isPremium === false && (
                      <span class="command-premium-badge">👑</span>
                    )}
                  </span>
                  {c.premiumOnly && isPremium === false ? (
                    <span class="nsfw-cmd-cost premium">{t('cmd.premium_only', 'Premium')}</span>
                  ) : privateShowIncluded ? (
                    privateShowCommandCostCredits > 0 ? (
                      <span class="nsfw-cmd-cost discounted">
                        <span class="cost-discounted">{privateShowCommandCostCredits}</span>
                        <IconStar class="starSvg" size={14} filled />
                      </span>
                    ) : (
                      <span class="nsfw-cmd-cost free">{t('private_show.included', 'Included')}</span>
                    )
                  ) : originalCost > 0 ? (
                    purchased.has(`${c.kind}:${c.command}`) ? (
                      <span class="nsfw-cmd-cost free">✓ {t('cmd.purchased', 'Purchased')}</span>
                    ) : premiumCommandsIncluded ? (
                      <span class="nsfw-cmd-cost free">{t('billing.included', 'Included')}</span>
                    ) : showDiscount ? (
                      <span class="nsfw-cmd-cost discounted">
                        <span class="cost-original">{originalCost}</span>
                        <span class="cost-discounted">{effectiveCost}</span>
                        <IconStar class="starSvg" size={14} filled />
                      </span>
                    ) : (
                      <span class="nsfw-cmd-cost">
                        {effectiveCost} <IconStar class="starSvg" size={14} filled />
                      </span>
                    )
                  ) : (
                    <span class="nsfw-cmd-cost free">{t('cmd.free', 'Free')}</span>
                  )}
                </button>
              );})}
            </div>
          </div>

          <ChatInput 
            onSend={handleSend} 
            placeholder={privateShowGateOpen ? t('private_show.locked_placeholder', 'Private show required') : t('chat.placeholder', 'Write something...')}
            disabled={actionsLocked || !playerReady} 
            billingLabel={privateShowMessageBillingLabel}
            billingTone={privateShowIncluded ? (privateShowMessageCostCredits > 0 ? 'paid' : 'free') : chatBillingTone}
          />
        </div>
      </div>


      {/* Paywall is now handled via TopupSheet in AppShell */}

      {debugEnabled && debugOpen ? (
        <div class="modalOverlay modalOverlayCentered" onClick={() => setDebugOpen(false)}>
          <div class="modalCard" onClick={(e) => e.stopPropagation()}>
            <div class="modalCardTitle">{t('debug.player_title', 'Player Debug')}</div>
            <div class="modalCardText" style={{ whiteSpace: 'pre-wrap', maxHeight: '50vh', overflow: 'auto' }}>
              {(controllerRef.current?.getBreadcrumbs?.() || [])
                .map((e) => {
                  const ts = new Date(e.t).toLocaleTimeString();
                  const data = e.data ? JSON.stringify(e.data) : '';
                  return `${ts}  ${e.tag}${data ? `  ${data}` : ''}`;
                })
                .join('\n') || t('debug.no_events', 'no events yet')}
            </div>
            <div class="modalActionsRow">
              <button
                class="secondaryBtn"
                onClick={async () => {
                  try {
                    const lines =
                      (controllerRef.current?.getBreadcrumbs?.() || [])
                        .map((e) => {
                          const ts = new Date(e.t).toISOString();
                          return `${ts}\t${e.tag}\t${e.data ? JSON.stringify(e.data) : ''}`;
                        })
                        .join('\n') || '';
                    await navigator.clipboard.writeText(lines || t('debug.no_events', 'no events yet'));
                  } catch {
                    // ignore
                  }
                }}
              >
                {t('btn.copy', 'Copy')}
              </button>
              <button class="secondaryBtn" onClick={() => setDebugOpen(false)}>
                {t('btn.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
